import { ClickAwayListener, Grid } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Expand,
  User,
  Dashboard,
  Checklist3,
  Reports1,
  Clipboard,
  TaskBoard,
} from '@HomaHealth/ui-component-library/lib/_AIQ-components';
import { Orb } from '@HomaHealth/ui-component-library/lib/_patient-access-components';
import IconButton from '@mui/material/IconButton';
import { patientAccessTheme } from '@HomaHealth/ui-component-library/lib/theme/theme';
import CollapsableList from '../../molecules/CollapsableList';
import SideNavBarTile from '../../molecules/SideNavBarTile';
import componentConstants from '../../../core-utils/messages/componentConstants';
import {
  ACCESS_DASHBOARD,
  HOME_SCREEN,
  COMPLIANCE_ASSURANCE,
  routeMap,
  SETTINGS,
  TASK_BOARD,
  USER_MANAGEMENT,
  DATA_CATALOGUE,
  CARE_NAVIGATOR,
  RISK_REGISTER,
  EXPERICENCE_SCREEN,
  ALL_REPORTS,
  CHAT,
} from '../../../core-utils/routes';
import { useStores } from '../../../stores';
import { observer } from 'mobx-react';
import { scrollBarStyles } from '../../../core-utils/messages/CommonConstants';
import { UserAccessConstants } from '../../../core-utils/helper-utils/userAccess';
import { toJS } from 'mobx';
import DataCatalogueIcon from '../../atoms/Icons/DataCatalogue';
import _ from 'lodash';
import CareNavigation from '../../atoms/Icons/CareNavigation';
import useSnackbar from '../../../core-utils/custom-hooks/Snackbar';
import Snackbar from '../../atoms/Snackbar';
import ExperienceIcon from '../../atoms/Icons/Experience';

export interface SideNavigationBarProps {
  disableUserMangement?: boolean;
  userAccessRoutes: string[];
}

const COLLAPSIBLE_PATHS = [
  EXPERICENCE_SCREEN,
  ACCESS_DASHBOARD,
  COMPLIANCE_ASSURANCE,
  ALL_REPORTS,
  RISK_REGISTER,
  TASK_BOARD,
  DATA_CATALOGUE,
  CARE_NAVIGATOR,
  CHAT,
  USER_MANAGEMENT,
];

const ICON_SIZE = { height: '24px', width: '24px' };

const useStyles = makeStyles()((theme) => ({
  containerBase: {
    height: 'calc(100vh - 59px)',
    borderRadius: '0px',
    background: patientAccessTheme.palette.navigationBar?.plainBackground,
    backgroundColor: patientAccessTheme.palette.status.white,
  },
  rootBase: {
    height: 'calc(100vh - 59px)',
    paddingTop: '40px',
    borderRight: `1px solid ${patientAccessTheme.palette.grey[300]}`,
    borderBottom: `1px solid ${patientAccessTheme.palette.grey[300]}`,
    overflowY: 'auto',
    overflowX: 'hidden',
    ...scrollBarStyles,
  },
  tileGrid: {
    margin: '0px 0px 12px 0px',
  },
  label: {
    fontWeight: 600,
    fontSize: '14px',
    color: patientAccessTheme.palette.navigationBar?.navTitle,
    lineHeight: '24px',
  },
  collapseButton: {
    width: '68px',
    height: '54px',
    background: patientAccessTheme.palette.navigationBar?.collapseIconBg,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
}));

const iconStroke = (currentPath: string, targetPath: string) =>
  currentPath === targetPath
    ? patientAccessTheme.palette.navigationBar?.navTitle
    : patientAccessTheme.palette.navigationBar?.inActiveIcon;

const SideNavigationBar: React.FC<SideNavigationBarProps> = observer(
  ({ disableUserMangement = false, userAccessRoutes }) => {
    const [isOpen, setIsOpen] = useState<boolean>(false);

    const { classes } = useStyles();

    const location = useLocation();
    const navigate = useNavigate();

    const constants = componentConstants.ORGANISMS.SIDENAVBAR;
    const menuItems = constants.MENU_ITEMS;

    const loginPageStore = useStores().loginPageStore;
    const employerGroupStore = useStores().employerGroupStore;
    const navigationStore = useStores().navigationStore;
    const pathStore = useStores().pathStore;

    const { snackbarState, handleCloseSnackBar } = useSnackbar();

    const [tabs, setTabs] = useState<any>(menuItems?.TABS ?? []);
    const [menuIndex, setMenuIndex] = useState<number>(-1);
    const [activeTabsArray, setActiveTabsArray] = useState<boolean[]>([]);
    const [initState, setInitState] = useState(
      routeMap.get(location?.pathname) ?? location?.pathname,
    );

    const updateActiveTabsArray = (newArray: boolean[]) => {
      if (!_.isEqual(activeTabsArray, newArray)) {
        setActiveTabsArray(newArray);
      }
    };

    const handleInitialState = (currentState: string) => {
      setInitState(currentState);
      navigationStore.setSelectedSideNavItem(currentState);
    };

    const handleActiveDropdownTab = (tabIndex: number) => {
      const next = Array(menuItems.TABS.length).fill(false);
      if (!activeTabsArray[tabIndex]) next[tabIndex] = true;
      updateActiveTabsArray(next);
    };

    const handleMenuOpen = (index: number) => setMenuIndex(index);
    const handleMenuClose = () => setMenuIndex(-1);

    useEffect(() => {
      const pathName: string =
        routeMap.get('/' + location?.pathname?.split('/')[1]) ??
        location.pathname;
      setInitState(pathName);

      const pathIndex = COLLAPSIBLE_PATHS.indexOf(pathName);
      const next = Array(menuItems.TABS.length).fill(false);
      if (pathIndex !== -1) next[pathIndex] = true;
      updateActiveTabsArray(next);
    }, [location]);

    useEffect(() => {
      if (!isOpen) setMenuIndex(-1);
    }, [isOpen]);

    useEffect(() => {
      if (disableUserMangement) {
        if (
          location.pathname !== SETTINGS &&
          location.pathname !== CARE_NAVIGATOR &&
          toJS(employerGroupStore?.employerGroupResponseData?.eg_nid).length ===
            0
        ) {
          navigate(HOME_SCREEN);
        }
        setTabs((prev: any) => prev.slice(0, -1));
      }
    }, [disableUserMangement]);

    useEffect(() => {
      if (!employerGroupStore?.isIndividualGroup) {
        setTabs((prev: any) => prev.slice(0, 2));
      }
    }, [employerGroupStore.isIndividualGroup]);

    useEffect(() => {
      if (
        loginPageStore.userResponse.role.name !==
        UserAccessConstants.SUPER_ADMIN
      ) {
        setTabs(menuItems.TABS.filter((tab: any) => tab[0] !== 'AI Assistant'));
      }
    }, [loginPageStore.userResponse.role.name]);

    const allIcons = useMemo(
      () => [
        <ExperienceIcon
          key="experience"
          stroke={iconStroke(initState, EXPERICENCE_SCREEN)}
          style={ICON_SIZE}
        />,
        <Dashboard
          key="dashboard"
          stroke={iconStroke(initState, ACCESS_DASHBOARD)}
          style={ICON_SIZE}
        />,
        <Checklist3
          key="checklist"
          stroke={iconStroke(initState, COMPLIANCE_ASSURANCE)}
          style={ICON_SIZE}
        />,
        <Reports1
          key="reports"
          stroke={iconStroke(initState, ALL_REPORTS)}
          style={ICON_SIZE}
        />,
        <TaskBoard
          key="taskboard"
          stroke={iconStroke(initState, RISK_REGISTER)}
          style={ICON_SIZE}
        />,
        <Clipboard
          key="clipboard"
          stroke={iconStroke(initState, TASK_BOARD)}
          style={ICON_SIZE}
        />,
        <DataCatalogueIcon
          key="datacatalogue"
          stroke={iconStroke(initState, DATA_CATALOGUE)}
          style={ICON_SIZE}
        />,
        <CareNavigation
          key="carenavigation"
          stroke={iconStroke(initState, CARE_NAVIGATOR)}
          style={ICON_SIZE}
        />,
        <Orb
          key="orb"
          stroke={iconStroke(initState, CHAT)}
          style={ICON_SIZE}
        />,
        <User
          key="user"
          stroke={iconStroke(initState, USER_MANAGEMENT)}
          style={ICON_SIZE}
        />,
      ],
      [initState],
    );

    const icons = useMemo(() => {
      if (
        loginPageStore.userResponse.role.name !==
        UserAccessConstants.SUPER_ADMIN
      ) {
        return allIcons.filter((_, i) => i !== allIcons.length - 2);
      }
      return allIcons;
    }, [allIcons, loginPageStore.userResponse.role.name]);

    const getAccessibleSubRoutes = (list: string[][]) =>
      list?.filter(
        (subArray) =>
          subArray[1] !== undefined && userAccessRoutes.includes(subArray[1]),
      ) ?? [];

    const subTiles = (tiles: any[]) =>
      tiles?.map((item: any, index: number) => (
        <Grid key={index} sx={{ margin: '0px 12px 12px 12px' }}>
          <SideNavBarTile
            label={isOpen ? item[0] : ''}
            onClick={() => setIsOpen(false)}
            isActive={location?.pathname === item[1]}
            startIcon={undefined}
            isOpen={isOpen}
            labelTextStyles={classes.label}
            showDropDownIcon={false}
            borderLeftString={
              location?.pathname === item[1]
                ? `5px solid ${patientAccessTheme.palette.navigationBar?.navTitle}`
                : 'none'
            }
            bgColorString={
              location?.pathname === item[1]
                ? patientAccessTheme.palette.navigationBar?.titleBg
                : 'inherit'
            }
            rootStyles={{ maxWidth: '239px' }}
            menu={getAccessibleSubRoutes(item[2])}
            tabIndex={index}
            menuIndex={menuIndex}
            path={item[1]}
            handleMenuOpen={handleMenuOpen}
            handleMenuClose={handleMenuClose}
          />
        </Grid>
      ));

    const getCollapsibleComponent = (item: any[], index: number) => (
      <CollapsableList
        subTiles={subTiles(item[2])}
        icon={icons[index]}
        mainTileLabel={item[0]}
        initialState={initState === item[3]}
        isOpen={isOpen}
        handleDropDownClick={() => {
          employerGroupStore?.removeSelectedDuration();
          pathStore.clearPath();
          navigate(item[3]);
          handleInitialState(item[3]);
          setIsOpen(false);
        }}
        currentState={activeTabsArray[index]}
        handleActiveDropdownTab={handleActiveDropdownTab}
        tabIndex={index}
        sideNavBarTileRootStyles={{ cursor: 'pointer' }}
      />
    );

    const navWidth = isOpen ? '250px' : 'fit-content';

    return (
      <>
        <ClickAwayListener onClickAway={() => setIsOpen(false)}>
          <div className={classes.containerBase} style={{ width: navWidth }}>
            <Grid
              container
              className={classes.rootBase}
              style={{ width: navWidth }}
              sx={{
                justifyContent: 'space-between',
                flexDirection: 'column',
                flexWrap: 'nowrap',
              }}
            >
              <Grid>
                {tabs.map((item: any, index: number) => (
                  <React.Fragment key={index}>
                    <div onMouseEnter={() => setMenuIndex(-1)}>
                      <Grid className={classes.tileGrid}>
                        {item[1] ? (
                          getCollapsibleComponent(item, index)
                        ) : item[2] ? (
                          <div
                            onClick={() => {
                              pathStore.clearPath();
                              navigate(item[2]);
                            }}
                          >
                            <SideNavBarTile
                              label={item[0]}
                              onClick={() => {
                                pathStore.clearPath();
                                handleInitialState(USER_MANAGEMENT);
                                setIsOpen(false);
                              }}
                              showDropDownIcon={item[1]}
                              startIcon={icons[index]}
                              labelTextStyles={classes.label}
                              isOpen={isOpen}
                              isActive={
                                initState === constants.USER_MANAGEMENT ||
                                location?.pathname?.includes(item[2])
                              }
                              borderLeftString={
                                initState === constants.USER_MANAGEMENT ||
                                location?.pathname?.includes(item[2])
                                  ? `5px solid ${patientAccessTheme.palette.navigationBar?.navTitle}`
                                  : 'none'
                              }
                              bgColorString={
                                initState === constants.USER_MANAGEMENT ||
                                location?.pathname?.includes(item[2])
                                  ? patientAccessTheme.palette.navigationBar
                                      ?.titleBg
                                  : 'inherit'
                              }
                            />
                          </div>
                        ) : null}
                      </Grid>
                    </div>
                  </React.Fragment>
                ))}
              </Grid>

              <Grid
                container
                sx={{ justifyContent: isOpen ? 'flex-end' : 'flex-start' }}
              >
                <Grid>
                  <div className={classes.collapseButton}>
                    <IconButton
                      onClick={() => setIsOpen((prev) => !prev)}
                      size="large"
                    >
                      <Expand
                        style={{
                          transform: isOpen ? 'rotate(180deg)' : 'none',
                          transition: 'transform 0.2s ease',
                        }}
                      />
                    </IconButton>
                  </div>
                </Grid>
              </Grid>
            </Grid>
          </div>
        </ClickAwayListener>
        <Snackbar
          {...snackbarState}
          handleClose={handleCloseSnackBar}
          vertical="bottom"
          horizontal="right"
        />
      </>
    );
  },
);

export default SideNavigationBar;
